import React from "react";
import {Link, Link as RouterLink} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {CustomCheckbox, modal} from "../../../util";
import {useLoading} from "../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../util/useManualPagination";
import {toast} from "react-toastify";
import dayjs from "dayjs";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import CancelIcon from '@material-ui/icons/Cancel';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import HomeIcon from '@material-ui/icons/Home';
import RedeemIcon from '@material-ui/icons/Redeem';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import PaginationBarForHandler from "../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const productTypes = Object.freeze({
    ALL : "ALL",
    VOUCHER : "VOUCHER",
    PROMO_CODE : "PROMO_CODE",
    MERCHANDISE : "MERCHANDISE",
});

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const formatDatetime = (dt) => {
    return dayjs(dt).format("DD MMM YYYY, hh:mma");
};

const OrderListingPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const [productType, setProductType] = React.useState(null);
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);

    const {setLoading} = useLoading();

    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
        voucher: 0,
        promoCode: 0,
        merchandise: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Order ID",
                accessor: "id",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.id}
                        </Link>
                    );
                },
            },
            {
                Header: "Ref",
                accessor: "ref",
            },
            {
                Header: "Customer Name",
                accessor: "name",
            },
            {
                Header: "Email",
                accessor: "email",
            },
            {
                Header: "Product Type",
                accessor: "productType",
                Cell: ({ row }) => {
                    if (row.original.productType === "VOUCHER") {
                        return <><RedeemIcon style={{ color: "#20C931", fontSize: "medium", verticalAlign: "bottom"}} /> Voucher</>;
                    } else if (row.original.productType === "MERCHANDISE") {
                        return <><LocalShippingIcon style={{ color: "#306F9D", fontSize: "medium", verticalAlign: "bottom"}} /> Merchandise</>;
                    } else if (row.original.productType === "PROMO_CODE") {
                        return <><LocalOfferIcon style={{ color: "#FF9800", fontSize: "medium", verticalAlign: "bottom"}} /> Coupon</>;
                    }
                    return row.original.productType || "";
                },
            },
            {
                Header: "Product Title",
                accessor: "title",
            },
            {
                Header: "Points Used",
                accessor: "pointUsed",
                Cell: ({ row }) => {
                    return <span>{row.original.pointUsed} pts</span>;
                },
            },
            {
                Header: "Redeem Date",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                        </div>
                    ),
                },
            ]);
        }
    );

    const getShopOrders = React.useCallback(async () => {
        try {
            setLoading(true);
            // Always fetch all orders without productType filter and unpaged
            const result = await apiHelper.cineclub.getShopOrders(
                0, // pageIndex
                0, // offset
                keyword,
                null, // Always null - no productType filter
                true // unpaged - fetch all data
            );
            // console.log('RESULT:', result)  
            
            var output = result.data.content.map(function(x){
                x.id = x.id.toString();
                return x;
            });
            
            // Store all orders in paginationHandler
            paginationHandler.setPageIndex(0).setRows(output);

            // Calculate counts from all orders
            const all = output.length;
            const voucher = output.filter((x) => x && x.productType == "VOUCHER").length;
            const promoCode = output.filter((x) => x && x.productType == "PROMO_CODE").length;
            const merchandise = output.filter((x) => x && x.productType == "MERCHANDISE").length;

            setElementCounts({
                all, voucher, promoCode, merchandise
            });

            // Apply current productType filter if any
            if (productType !== null) {
                const targetProductType = productType.toUpperCase();
                paginationHandler
                    .resetRows()
                    .setQueryStatus("productType", targetProductType)
                    .filterRowsByAll();
                const filteredCount = productType === 'voucher' ? voucher : 
                                     productType === 'promocode' ? promoCode : 
                                     productType === 'merchandise' ? merchandise : all;
                setTotalPages(Math.ceil(filteredCount/paginationHandler.pageSize));
                setData(paginationHandler.setPageIndex(0).getRows());
            } else {
                setData(paginationHandler.setPageIndex(0).getRows());
                setTotalPages(Math.ceil(all/paginationHandler.pageSize));
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading, keyword]);

    React.useEffect(() => {
        if (paginationHandler) {
            getShopOrders(); // only get orders if paginationHandler ready
        }
    }, [getShopOrders, paginationHandler]);

    // Update data when pageIndex changes (for pagination)
    React.useEffect(() => {
        if (paginationHandler && paginationHandler.rows && paginationHandler.rows.length > 0) {
            // Update paginationHandler's pageIndex to match react-table's pageIndex
            paginationHandler.setPageIndex(pageIndex);
            // Get the current page's data
            const currentPageData = paginationHandler.getRows();
            setData(currentPageData);
        }
    }, [pageIndex, paginationHandler]);

    const productTypeFilterHandler = (targetProductType) => () => {
        // Filter client-side without calling API
        var filteredProductType = targetProductType != null ? lowerCaseProductType(targetProductType) : null;
        setProductType(filteredProductType);

        if (targetProductType != null) {
            paginationHandler
                .resetRows()
                .setQueryStatus("productType", targetProductType)
                .filterRowsByAll();
            // Map the filtered product type to the correct elementCounts key
            let countKey;
            if (targetProductType === "VOUCHER") {
                countKey = "voucher";
            } else if (targetProductType === "PROMO_CODE") {
                countKey = "promoCode";
            } else if (targetProductType === "MERCHANDISE") {
                countKey = "merchandise";
            } else {
                countKey = "all";
            }
            var filteredElementCount = elementCounts[countKey] || 0;
            setTotalPages(Math.ceil(filteredElementCount/paginationHandler.pageSize));
        } else {
            // Show all - reset to original rows
            paginationHandler.resetRows();
            setTotalPages(Math.ceil(elementCounts.all/paginationHandler.pageSize));
        }

        gotoPage(0);
        setData(paginationHandler.setPageIndex(0).getRows());
    };

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        console.log("KEYWORD: "+keyword);
        paginationHandler
            .resetRows()
            .setQueryString(["email","id","name","title"], keyword)
            .filterRowsByAll();
        setData(paginationHandler.setPageIndex(0).getRows());
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading]);

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            default:
                break;
        }
    }, [actionType, selectedFlatRows, getShopOrders]);


    function lowerCaseProductType(productType){
        return productType.replace('_','').toLowerCase();
    }

    function getKeyword(keyword) {
        return keyword ? `&keyword=${keyword}` : "";
    }

    function getProductType(productType) {
        return productType ? `&productType=${productType}` : "";
    }

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Cineclub > Order List"}
            </Typography>
            <form>
                <StatusFilter>
                    <li>
                        <button
                            className={productType === null ? "active" : ""}
                            type="button"
                            onClick={productTypeFilterHandler(null)}
                        >
                            <Typography variant="p">All
                                ({elementCounts.all})</Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={productType === lowerCaseProductType(productTypes.VOUCHER) ? "active" : ""}
                            type="button"
                            onClick={productTypeFilterHandler(productTypes.VOUCHER)}
                        >
                            <Typography variant="p">
                                Vouchers
                                ({elementCounts.voucher})
                            </Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={productType === lowerCaseProductType(productTypes.PROMO_CODE) ? "active" : ""}
                            type="button"
                            onClick={productTypeFilterHandler(productTypes.PROMO_CODE)}
                        >
                            <Typography variant="p">
                                Coupons
                                ({elementCounts.promoCode})
                            </Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={productType === lowerCaseProductType(productTypes.MERCHANDISE) ? "active" : ""}
                            type="button"
                            onClick={productTypeFilterHandler(productTypes.MERCHANDISE)}
                        >
                            <Typography variant="p">
                                Merchandise
                                ({elementCounts.merchandise})
                            </Typography>
                        </button>
                    </li>
                </StatusFilter>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        {/* <Box
                            width="100%"
                            component="span"
                            display="flex"
                            alignItems="center"
                        >
                            <Box flex>
                                <FormControl fullWidth
                                             margin="dense"
                                             variant="outlined">
                                    <InputLabel
                                        id="demo-simple-select-outlined-label">
                                        Bulk Actions
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-outlined-label"
                                        id="demo-simple-select-outlined"
                                        value={actionType}
                                        onChange={handleSelectChange}
                                        label="Bulk Actions"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box flex>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleBulkAction}
                                    disabled={disabled}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Box> */}
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex" style={{ marginRight: "10px" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={"a"}
                                    disabled={disabled}
                                    href={`${
                                        process.env.REACT_APP_API_HOSTNAME ||
                                        "https://hkiff-api-dev.cloud.udev.hk"
                                    }/membership/admin/shop/orders/excelExport?unpaged=true${getKeyword(
                                        keyword
                                    )}${getProductType(productType === null ? null : productType.toUpperCase())}`}
                                >
                                    Export List
                                </Button>
                            </Box>
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "1%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "8%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "13%"}}/>
                                    <col
                                        style={{width: "13%"}}/>
                                    <col
                                        style={{width: "25%"}} />
                                    <col
                                        style={{width: "5%"}} />
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, index) => {
                                        prepareRow(row);
                                        return (
                                            <TableRow {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <TableCell
                                                            {...cell.getCellProps()}
                                                            className={`${cell.column.className ?? ""}`}
                                                            style={{
                                                                paddingTop: 6,
                                                                paddingBottom: 6,
                                                                lineHeight: "1.1em",
                                                                height: "48px",
                                                            }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={productType}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
};

export default OrderListingPage;
